import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import { CheckIcon, UserIcon } from 'lucide-react';
const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="pt-20" data-id="element-130">
      {/* Hero Section */}
      <section className="bg-neutral-light py-20" data-id="element-131">
        <div className="container mx-auto px-4 md:px-6" data-id="element-132">
          <div className="max-w-3xl mx-auto text-center" data-id="element-133">
            <motion.h1 initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl md:text-5xl font-display font-bold mb-6" data-id="element-134">
              About{' '}
              <span className="text-toronto-blue" data-id="element-135">Market Research Toronto</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="text-xl text-neutral-dark/80" data-id="element-136">
              Delivering global-standard research with deep local insights since
              2008.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Mission & Vision */}
      <section className="py-16 bg-white" data-id="element-137">
        <div className="container mx-auto px-4 md:px-6" data-id="element-138">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-139">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-140">
              <h2 className="text-3xl font-display font-bold mb-6" data-id="element-141">
                Our Mission & Vision
              </h2>
              <div className="mb-8" data-id="element-142">
                <h3 className="text-xl font-semibold mb-3 text-toronto-blue" data-id="element-143">
                  Mission
                </h3>
                <p className="text-neutral-dark/80" data-id="element-144">
                  To provide organizations with actionable, data-driven insights
                  that reflect Toronto's unique multicultural landscape while
                  meeting global research standards.
                </p>
              </div>
              <div className="mb-8" data-id="element-145">
                <h3 className="text-xl font-semibold mb-3 text-toronto-blue" data-id="element-146">
                  Vision
                </h3>
                <p className="text-neutral-dark/80" data-id="element-147">
                  To be the premier research partner for organizations seeking
                  to understand and succeed in Toronto's diverse markets and to
                  export our multicultural research expertise globally.
                </p>
              </div>
              <div data-id="element-148">
                <h3 className="text-xl font-semibold mb-3 text-toronto-blue" data-id="element-149">
                  Values
                </h3>
                <ul className="grid grid-cols-2 gap-3" data-id="element-150">
                  {['Accuracy', 'Innovation', 'Cultural Sensitivity', 'Integrity', 'Collaboration', 'Excellence'].map((value, index) => <li key={index} className="flex items-center" data-id="element-151">
                      <CheckIcon size={16} className="text-toronto-blue mr-2" data-id="element-152" />
                      <span data-id="element-153">{value}</span>
                    </li>)}
                </ul>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-154">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Team collaboration" className="rounded-lg shadow-lg w-full h-auto" data-id="element-155" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Methodology */}
      <section className="py-16 bg-neutral-light/50" data-id="element-156">
        <div className="container mx-auto px-4 md:px-6" data-id="element-157">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-158">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4" data-id="element-159">
              Our Methodology
            </h2>
            <p className="text-lg text-neutral-dark/80" data-id="element-160">
              We combine rigorous research practices with innovative approaches
              to deliver insights that drive results.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-161">
            {[{
            title: 'Data Collection',
            description: 'We utilize multiple channels including surveys, interviews, focus groups, and digital analytics to gather comprehensive data.'
          }, {
            title: 'Analysis & Interpretation',
            description: 'Our team of analysts applies statistical rigor and cultural context to transform raw data into meaningful insights.'
          }, {
            title: 'Reporting & Implementation',
            description: 'We deliver clear, actionable reports and work with clients to implement data-driven strategies that achieve measurable results.'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-white p-6 rounded-lg shadow-md" data-id="element-162">
                <div className="bg-toronto-blue/10 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto text-toronto-blue" data-id="element-163">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-center" data-id="element-164">
                  {item.title}
                </h3>
                <p className="text-neutral-dark/80 text-center" data-id="element-165">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Leadership Team */}
      <section className="py-16 bg-white" data-id="element-166">
        <div className="container mx-auto px-4 md:px-6" data-id="element-167">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-168">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4" data-id="element-169">
              Our Leadership Team
            </h2>
            <p className="text-lg text-neutral-dark/80" data-id="element-170">
              Meet the experienced professionals who lead our research
              initiatives.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-171">
            {[{
            name: 'Dr. Sarah Chen',
            title: 'Founder & CEO',
            bio: 'With over 20 years of experience in market research and a PhD in Consumer Psychology, Dr. Chen leads our strategic vision.',
            image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80'
          }, {
            name: 'Michael Rodriguez',
            title: 'Director of Research',
            bio: 'Michael specializes in quantitative methodologies and oversees the design and implementation of our research projects.',
            image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80'
          }, {
            name: 'Aisha Patel',
            title: 'Director of Multicultural Insights',
            bio: "With expertise in cross-cultural research, Aisha ensures our methodologies capture Toronto's diverse perspectives.",
            image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80'
          }, {
            name: 'David Kim',
            title: 'Director of Data Analytics',
            bio: 'David leads our data science team, applying advanced analytics and AI to extract meaningful insights from complex datasets.',
            image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80'
          }, {
            name: 'Olivia Thompson',
            title: 'Director of Client Services',
            bio: 'Olivia ensures that our research translates into actionable strategies that deliver measurable results for our clients.',
            image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80'
          }, {
            name: 'Jean-Pierre Leblanc',
            title: 'Director of Bilingual Research',
            bio: 'Jean-Pierre specializes in bilingual research methodologies, ensuring we serve both English and French-speaking populations.',
            image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80'
          }].map((person, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-neutral-light p-6 rounded-lg shadow-md text-center" data-id="element-172">
                <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4" data-id="element-173">
                  <img src={person.image} alt={person.name} className="w-full h-full object-cover" data-id="element-174" />
                </div>
                <h3 className="text-xl font-semibold mb-1" data-id="element-175">{person.name}</h3>
                <p className="text-toronto-blue font-medium mb-3" data-id="element-176">
                  {person.title}
                </p>
                <p className="text-neutral-dark/80" data-id="element-177">{person.bio}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-16 bg-toronto-blue text-white" data-id="element-178">
        <div className="container mx-auto px-4 md:px-6" data-id="element-179">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-180">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4" data-id="element-181">
              Client Testimonials
            </h2>
            <p className="text-lg opacity-90" data-id="element-182">
              Hear from organizations that have partnered with Market Research
              Toronto.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-183">
            {[{
            quote: "Market Research Toronto's insights into our target demographic were invaluable. Their multicultural approach helped us connect with Toronto's diverse communities in ways we hadn't previously considered.",
            author: 'Jennifer Lee',
            position: 'Marketing Director, Canadian Retail Corporation',
            image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80'
          }, {
            quote: "The depth of analysis provided by the team went far beyond what we expected. They didn't just deliver data – they delivered actionable recommendations that have transformed our approach to the Toronto market.",
            author: 'Marcus Johnson',
            position: 'CEO, Urban Development Partners',
            image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80'
          }].map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-white text-neutral-dark p-6 rounded-lg shadow-md" data-id="element-184">
                <div className="flex items-center mb-4" data-id="element-185">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-186">
                    <img src={testimonial.image} alt={testimonial.author} className="w-full h-full object-cover" data-id="element-187" />
                  </div>
                  <div data-id="element-188">
                    <h4 className="font-semibold" data-id="element-189">{testimonial.author}</h4>
                    <p className="text-sm text-neutral-dark/70" data-id="element-190">
                      {testimonial.position}
                    </p>
                  </div>
                </div>
                <p className="italic" data-id="element-191">{testimonial.quote}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-white" data-id="element-192">
        <div className="container mx-auto px-4 md:px-6" data-id="element-193">
          <div className="max-w-3xl mx-auto text-center" data-id="element-194">
            <h2 className="text-3xl font-display font-bold mb-6" data-id="element-195">
              Ready to Work With Us?
            </h2>
            <p className="text-lg text-neutral-dark/80 mb-8" data-id="element-196">
              Partner with Market Research Toronto to gain the insights you need
              to succeed in Toronto's diverse marketplace.
            </p>
            <Button variant="primary" size="lg" data-id="element-197">
              Contact Our Team
            </Button>
          </div>
        </div>
      </section>
    </div>;
};
export default About;